import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
    useController,
    type Control,
    type FieldValues,
    type Path
} from 'react-hook-form';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { PhraseLibrary } from '@ast-data/shared';
import { useValidationMessage } from '../i18n/validationMessages';
import { useDictation } from './useDictation';
import { capitalizeSentences } from '../utils/sentenceCase';
import PhrasePickerPanel from './PhrasePickerPanel';
import EmptyFieldWarning from './EmptyFieldWarning';
import RefTextEditor, {
    type RefTextEditorHandle
} from './refEditor/RefTextEditor';
import type { FindingRefRange } from './refEditor/findingRefRanges';
import { useFieldTranslation } from '../features/translation/useFieldTranslation';
import { useRecordTranslationsScope } from '../features/translation/recordTranslations';
import { VoiceDictationButton } from '../features/translation/VoiceDictationButton';
import type { VoiceResult } from '../features/translation/useVoiceDictation';
import type { FreshRange } from './refEditor/FreshTextDecoration';
import TextareaHighlight from './TextareaHighlight';
import { useDictationBusyReport } from './dictationBusy';
import {
    TranslateMenuButton,
    TranslationErrorLine,
    TranslationStatusBar,
    TranslationViewToggle,
    type TranslationView
} from '../features/translation/TranslationTools';

/** Where an insert lands: the text around the caret, and the gap before it. */
interface InsertAnchor {
    before: string;
    after: string;
    separator: string;
}

/** How long freshly dictated words stay tinted after the recording ends. */
const FRESH_FOR_MS = 4000;

/** Keys that reach a field without putting its caret anywhere. */
const CARET_NEUTRAL_KEYS = new Set([
    'Tab',
    'Shift',
    'Control',
    'Alt',
    'Meta',
    'Escape'
]);

interface RichTextAreaProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: Path<TFieldValues>;
    label: string;
    /**
     * Icon shown left of the label. Sized and coloured by the header, which
     * tracks the label including its error state, so pass a bare element —
     * `icon={<NotesIcon />}`, no props needed.
     */
    icon?: ReactNode;
    /** Rows shown inline (the fullscreen editor always uses a tall textarea). */
    minRows?: number;
    /** Character-count ceiling: shown in the footer and enforced on input. */
    maxLength?: number;
    /** Optional Textbausteine — enables the toolbar's phrase-library button. */
    library?: PhraseLibrary;
    /** Show the soft "recommended field is empty" hint while empty. */
    recommended?: boolean;
    disabled?: boolean;
    /**
     * Shown for reading only: the text stays selectable, the tools and the
     * counter go away. See formFields/readOnlySheet.
     */
    readOnly?: boolean;
    /**
     * Mark the Befunde the text names — a ring round the reference, its number
     * bold, a link where the record exists.
     *
     * OPT-IN because it swaps the `<textarea>` for a TipTap editor (see
     * components/refEditor). Everything around it is untouched, and so is the
     * value, which stays plain text.
     */
    findingRefs?: FindingRefSupport;
}

/** What the field needs in order to resolve and open the references it marks. */
export interface FindingRefSupport {
    /** Whether the reference points at a record that exists. */
    resolves: (ref: FindingRefRange) => boolean;
    onOpen: (ref: FindingRefRange) => void;
    /** Tooltip for a linked reference — it is what announces Ctrl+click. */
    titleFor: (ref: FindingRefRange) => string;
}

/** First `[…]` placeholder in a phrase, so it can be selected for overwriting. */
function firstPlaceholder(text: string): { start: number; end: number } | null {
    const match = /\[[^\]]*\]/.exec(text);
    if (!match) {
        return null;
    }
    return { start: match.index, end: match.index + match[0].length };
}

interface RichTextAreaBodyProps<
    TFieldValues extends FieldValues
> extends RichTextAreaProps<TFieldValues> {
    /** True inside the fullscreen dialog (tall textarea, no fullscreen button). */
    fullscreen: boolean;
    onToggleFullscreen: () => void;
    /** Shared by both instances, so the fullscreen editor opens on the same. */
    view: TranslationView;
    onViewChange: (view: TranslationView) => void;
}

/**
 * Rendered both inline and, unchanged, inside the fullscreen dialog — each
 * instance owns its own caret, dictation and phrase-panel state while sharing the
 * same RHF field value.
 */
function RichTextAreaBody<TFieldValues extends FieldValues>({
    control,
    name,
    label,
    icon,
    minRows = 4,
    maxLength,
    library,
    recommended = false,
    disabled = false,
    readOnly = false,
    findingRefs,
    fullscreen,
    onToggleFullscreen,
    view,
    onViewChange
}: RichTextAreaBodyProps<TFieldValues>) {
    const intl = useIntl();
    const validationMessage = useValidationMessage();
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    // Only one of the two is live: `findingRefs` decides which element the
    // value is edited in, and the caret helpers below read whichever it is.
    const editorRef = useRef<RefTextEditorHandle | null>(null);
    // Whether anyone has put the caret into the text. Until then a textarea
    // still reports position 0, and a dictation would land at the START.
    // Tabbing in does not count: the caret then sits at 0 too.
    const caretPlacedRef = useRef(false);
    const { field, fieldState } = useController({ name, control });
    const dictation = useDictation();
    const [phrasesOpen, setPhrasesOpen] = useState(false);

    const value = String(field.value ?? '');
    const isEmpty = value.length === 0;
    // Null outside a form that keeps translations — the field then has none
    // of the tools below.
    const fieldTranslation = useFieldTranslation(name, value);
    const translationScope = useRecordTranslationsScope();
    const translation = fieldTranslation?.translation ?? null;
    const showTranslation = view === 'translation' && translation !== null;
    const translationReadOnly = readOnly || !fieldTranslation?.canReview;
    const shownLength = showTranslation
        ? translation.text.length
        : value.length;
    const errorMessage = fieldState.error
        ? validationMessage(fieldState.error.message)
        : undefined;

    /**
     * A click, an arrow key or typing in the ORIGINAL puts its caret — not in
     * the translation view, whose caret is another element's.
     */
    function markCaretPlaced(target: EventTarget) {
        if (
            !showTranslation &&
            (target as HTMLElement).closest?.(
                'textarea, [contenteditable="true"]'
            )
        ) {
            caretPlacedRef.current = true;
        }
    }

    /**
     * Where an insert goes: at the caret, or — where nobody has put the caret
     * yet — at the end, so a field nobody has clicked into is added to, not
     * prefixed. Taken once per insert; a dictation keeps it for its whole run.
     */
    function insertAnchor(): InsertAnchor {
        const placedCursor = findingRefs
            ? editorRef.current?.getCaret()
            : inputRef.current?.selectionStart;
        const cursor = caretPlacedRef.current
            ? (placedCursor ?? value.length)
            : value.length;
        const before = value.slice(0, cursor);
        return {
            before,
            after: value.slice(cursor),
            // Separate from preceding text unless it already ends in whitespace.
            separator: before.length > 0 && !/\s$/.test(before) ? ' ' : ''
        };
    }

    /**
     * The field's text with `text` at `anchor`, and where it landed.
     * `sentenceCase` is for dictation: the recognizer never sees what is
     * already in the field, so a phrase following a full stop arrives
     * lowercase. Capitalization keeps the length, so offsets into `text`
     * still line up.
     */
    function spliceAt(
        anchor: InsertAnchor,
        text: string,
        { sentenceCase = false, trailingSpace = true } = {}
    ) {
        const cased = sentenceCase
            ? capitalizeSentences(text, anchor.before)
            : text;
        const insert = trailingSpace ? `${cased} ` : cased;
        let next = `${anchor.before}${anchor.separator}${insert}${anchor.after}`;
        if (maxLength) {
            next = next.slice(0, maxLength);
        }
        const start = anchor.before.length + anchor.separator.length;
        return {
            next,
            start,
            insert,
            // The new words themselves, without the space after them.
            fresh: {
                start,
                end: Math.min(start + cased.length, next.length)
            }
        };
    }

    /** Select `start`–`end` once the new value has reached the field. */
    function placeCaret(start: number, end: number) {
        // Put there on purpose: the next insert continues from it.
        caretPlacedRef.current = true;
        requestAnimationFrame(() => {
            if (findingRefs) {
                // The editor clamps to its own length; the offsets are the
                // characters of the value it was just handed.
                editorRef.current?.selectRange(start, end);
                return;
            }
            const node = inputRef.current;
            if (!node) {
                return;
            }
            node.focus();
            node.setSelectionRange(
                Math.min(start, node.value.length),
                Math.min(end, node.value.length)
            );
        });
    }

    /**
     * Splice `text` in (see insertAnchor), then place the caret on a `[…]`
     * placeholder (selected, so the next keystroke overwrites it) or just past
     * the insert. Returns where the new words landed.
     */
    function insertAtCursor(
        text: string,
        options: { selectPlaceholder?: boolean; sentenceCase?: boolean } = {}
    ): FreshRange {
        const { selectPlaceholder = false, sentenceCase = false } = options;
        const { next, start, insert, fresh } = spliceAt(insertAnchor(), text, {
            sentenceCase
        });
        field.onChange(next);
        const placeholder = selectPlaceholder ? firstPlaceholder(text) : null;
        if (placeholder) {
            placeCaret(start + placeholder.start, start + placeholder.end);
        } else {
            placeCaret(start + insert.length, start + insert.length);
        }
        return fresh;
    }

    // --- DeepL dictation, written into the field as it is spoken. ---

    /** The running dictation: where it writes, and the text before it. */
    const voiceRef = useRef<{
        anchor: InsertAnchor;
        previousText: string;
    } | null>(null);
    /** The words just dictated, tinted for a moment (see FRESH_FOR_MS). */
    const [fresh, setFresh] = useState<FreshRange | null>(null);
    /** No typing while DeepL writes — it revises the words it is unsure of. */
    const [voiceLocked, setVoiceLocked] = useState(false);
    const freshTimerRef = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(freshTimerRef.current), []);

    // The form holds its save while DeepL still writes here.
    const reportDictation = useDictationBusyReport();
    useEffect(() => {
        if (!voiceLocked || !reportDictation) {
            return;
        }
        reportDictation(true);
        return () => reportDictation(false);
    }, [voiceLocked, reportDictation]);

    /** Tint `range` for a moment — what a dictation has just put in. */
    function markFresh(range: FreshRange) {
        window.clearTimeout(freshTimerRef.current);
        setFresh(range);
        freshTimerRef.current = window.setTimeout(
            () => setFresh(null),
            FRESH_FOR_MS
        );
    }

    function startVoice() {
        window.clearTimeout(freshTimerRef.current);
        voiceRef.current = { anchor: insertAnchor(), previousText: value };
        setVoiceLocked(true);
        setFresh(null);
    }

    /** What DeepL has so far, its last guesses included, in place. */
    function showVoice(text: string) {
        const voice = voiceRef.current;
        if (!voice) {
            return;
        }
        if (text === '') {
            field.onChange(voice.previousText);
            setFresh(null);
            return;
        }
        const spliced = spliceAt(voice.anchor, text, {
            sentenceCase: true,
            trailingSpace: false
        });
        field.onChange(spliced.next);
        setFresh(spliced.fresh);
    }

    /**
     * The final text — and, translated, its German as the translation. The
     * field (and the form's save) stays locked until that German is in: a save
     * in between would commit the translations without it.
     */
    function finishVoice(result: VoiceResult) {
        const voice = voiceRef.current;
        if (!voice) {
            return;
        }
        voiceRef.current = null;
        const spliced = spliceAt(voice.anchor, result.text, {
            sentenceCase: true
        });
        field.onChange(spliced.next);
        markFresh(spliced.fresh);
        placeCaret(
            spliced.start + spliced.insert.length,
            spliced.start + spliced.insert.length
        );
        if (!result.translation || !fieldTranslation) {
            setVoiceLocked(false);
            return;
        }
        void fieldTranslation
            .addDictated({
                previousText: voice.previousText,
                nextText: spliced.next,
                insertAt: voice.anchor.before.length,
                translation: result.translation,
                sourceLang: result.sourceLang
            })
            .finally(() => setVoiceLocked(false));
    }

    /**
     * Ended without a text (nothing said, or it failed): as it was. After a
     * text, finishVoice has taken over and releases the lock itself.
     */
    function endVoice() {
        const voice = voiceRef.current;
        if (!voice) {
            return;
        }
        voiceRef.current = null;
        setVoiceLocked(false);
        field.onChange(voice.previousText);
        setFresh(null);
    }

    const counterText = maxLength
        ? `${shownLength.toLocaleString('de-DE')} / ${maxLength.toLocaleString('de-DE')}`
        : shownLength.toLocaleString('de-DE');

    const hasError = !!fieldState.error;

    return (
        // One bordered frame so the toolbar, textarea and footer read as a single
        // field; the border tracks focus (primary) and error (red) like an input.
        <Box
            onPointerUp={(event) => markCaretPlaced(event.target)}
            onKeyUp={(event) => {
                if (!CARET_NEUTRAL_KEYS.has(event.key)) {
                    markCaretPlaced(event.target);
                }
            }}
            sx={{
                border: 1,
                borderColor: hasError ? 'error.main' : 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                transition: 'border-color 120ms ease, box-shadow 120ms ease',
                '&:focus-within': {
                    borderColor: hasError ? 'error.main' : 'primary.main',
                    boxShadow: (theme) =>
                        `inset 0 0 0 1px ${
                            hasError
                                ? theme.palette.error.main
                                : theme.palette.primary.main
                        }`
                },
                ...(fullscreen && {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                })
            }}
        >
            {/* Header: icon + label (left) + the toolbar tools (right). */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    pl: 1.5,
                    pr: 0.5,
                    py: 0.25,
                    minHeight: 44,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                {icon && (
                    // Sized and coloured here, so every field's icon matches its
                    // label. It labels what the text already names, so it stays
                    // out of the accessibility tree.
                    <Box
                        aria-hidden
                        sx={{
                            display: 'flex',
                            flexShrink: 0,
                            // Not the header's `gap`: that is shared with the
                            // toolbar buttons on the right.
                            mr: 0.75,
                            color: hasError ? 'error.main' : 'text.secondary',
                            '& > *': { fontSize: 20 }
                        }}
                    >
                        {icon}
                    </Box>
                )}
                <Typography
                    component="span"
                    variant="subtitle2"
                    sx={{
                        mr: 'auto',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: hasError ? 'error.main' : 'text.secondary'
                    }}
                >
                    {label}
                </Typography>
                {recommended && isEmpty && !readOnly && !showTranslation && (
                    <EmptyFieldWarning
                        bare
                        message={intl.formatMessage({
                            id: 'common.recommendedEmpty'
                        })}
                    />
                )}
                {library && !readOnly && !showTranslation && (
                    <Button
                        size="small"
                        startIcon={<MenuBookIcon />}
                        endIcon={
                            phrasesOpen ? (
                                <ExpandLessIcon />
                            ) : (
                                <ExpandMoreIcon />
                            )
                        }
                        onClick={() => setPhrasesOpen((prev) => !prev)}
                        disabled={disabled || voiceLocked}
                    >
                        {intl.formatMessage({ id: 'phrases.open' })}
                    </Button>
                )}
                {dictation.supported && !readOnly && !showTranslation && (
                    <IconButton
                        size="small"
                        color={dictation.listening ? 'error' : 'default'}
                        disabled={disabled || voiceLocked}
                        aria-label={intl.formatMessage({
                            id: dictation.listening
                                ? 'field.speech.stop'
                                : 'field.speech.start'
                        })}
                        onClick={() =>
                            dictation.toggle((text) =>
                                markFresh(
                                    insertAtCursor(text, { sentenceCase: true })
                                )
                            )
                        }
                    >
                        {dictation.listening ? (
                            <StopIcon fontSize="small" />
                        ) : (
                            <MicIcon fontSize="small" />
                        )}
                    </IconButton>
                )}
                {!readOnly && !showTranslation && (
                    <VoiceDictationButton
                        disabled={disabled || dictation.listening}
                        canTranslate={!!fieldTranslation?.canReview}
                        projectId={translationScope?.projectId}
                        onStart={startVoice}
                        onLive={showVoice}
                        onResult={finishVoice}
                        onEnd={endVoice}
                    />
                )}
                {fieldTranslation && (
                    <TranslationViewToggle
                        fieldTranslation={fieldTranslation}
                        view={showTranslation ? 'translation' : 'original'}
                        onChange={onViewChange}
                        disabled={voiceLocked}
                    />
                )}
                {fieldTranslation?.serviceEnabled && (
                    <TranslateMenuButton
                        fieldTranslation={fieldTranslation}
                        onTranslated={() => onViewChange('translation')}
                        disabled={voiceLocked}
                    />
                )}
                {!readOnly && (
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({
                            id: fullscreen
                                ? 'common.exitFullscreen'
                                : 'common.enterFullscreen'
                        })}
                        onClick={onToggleFullscreen}
                        disabled={voiceLocked}
                    >
                        {fullscreen ? (
                            <FullscreenExitIcon fontSize="small" />
                        ) : (
                            <FullscreenIcon fontSize="small" />
                        )}
                    </IconButton>
                )}
            </Box>

            {fieldTranslation && (
                <TranslationErrorLine fieldTranslation={fieldTranslation} />
            )}
            {fieldTranslation && showTranslation && (
                <TranslationStatusBar fieldTranslation={fieldTranslation} />
            )}

            {library && (
                <Collapse
                    in={phrasesOpen && !showTranslation && !voiceLocked}
                    unmountOnExit
                >
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <PhrasePickerPanel
                            library={library}
                            onSelect={(phrase) =>
                                insertAtCursor(phrase, {
                                    selectPlaceholder: true
                                })
                            }
                        />
                    </Box>
                </Collapse>
            )}

            {showTranslation && findingRefs ? (
                /* The translation of a field that marks its references marks
                   them too — a Befund named in the original is named in the
                   translation. Still not the form's value: it is edited
                   through the translation scope. */
                <RefTextEditor
                    key="translation"
                    value={translation.text}
                    onChange={(next) =>
                        fieldTranslation?.edit(
                            maxLength ? next.slice(0, maxLength) : next
                        )
                    }
                    disabled={disabled}
                    readOnly={translationReadOnly}
                    minRows={fullscreen ? 18 : minRows}
                    fullscreen={fullscreen}
                    ariaLabel={label}
                    resolves={findingRefs.resolves}
                    onOpen={findingRefs.onOpen}
                    titleFor={findingRefs.titleFor}
                />
            ) : showTranslation ? (
                /* The translation is not the form's value: it is edited
                   through the translation scope, and stays plain text. */
                <InputBase
                    fullWidth
                    multiline
                    minRows={fullscreen ? 18 : minRows}
                    disabled={disabled}
                    readOnly={translationReadOnly}
                    value={translation.text}
                    onChange={(event) =>
                        fieldTranslation?.edit(
                            maxLength
                                ? event.target.value.slice(0, maxLength)
                                : event.target.value
                        )
                    }
                    inputProps={{
                        'aria-label': label,
                        lang: translation.targetLang,
                        ...(maxLength ? { maxLength } : {})
                    }}
                    sx={{
                        px: 1.5,
                        py: 1,
                        alignItems: 'flex-start',
                        bgcolor: (theme) =>
                            translationReadOnly
                                ? 'transparent'
                                : theme.palette.mode === 'light'
                                  ? 'rgba(0, 0, 0, 0.06)'
                                  : 'rgba(255, 255, 255, 0.09)',
                        ...(fullscreen && { flexGrow: 1, overflow: 'auto' })
                    }}
                />
            ) : findingRefs ? (
                /* Same slot, same metrics — only the element differs. maxLength
                   is enforced on the way out of the editor instead of by
                   attribute. */
                <RefTextEditor
                    key="original"
                    value={value}
                    onChange={(next) =>
                        field.onChange(
                            maxLength ? next.slice(0, maxLength) : next
                        )
                    }
                    onBlur={field.onBlur}
                    disabled={disabled}
                    readOnly={readOnly}
                    placeholder={readOnly ? '—' : undefined}
                    minRows={fullscreen ? 18 : minRows}
                    fullscreen={fullscreen}
                    ariaLabel={label}
                    resolves={findingRefs.resolves}
                    onOpen={findingRefs.onOpen}
                    titleFor={findingRefs.titleFor}
                    handleRef={editorRef}
                    freshRange={fresh}
                    locked={voiceLocked}
                />
            ) : (
                <InputBase
                    fullWidth
                    multiline
                    minRows={fullscreen ? 18 : minRows}
                    disabled={disabled}
                    readOnly={readOnly || voiceLocked}
                    placeholder={readOnly ? '—' : undefined}
                    name={field.name}
                    endAdornment={
                        <TextareaHighlight value={value} range={fresh} />
                    }
                    value={value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={(element: HTMLTextAreaElement | null) => {
                        field.ref(element);
                        inputRef.current = element;
                    }}
                    inputProps={{
                        'aria-label': label,
                        // Tells a phone keyboard's own mic and its typing
                        // autocorrect to start sentences upper-case.
                        autoCapitalize: 'sentences',
                        ...(maxLength ? { maxLength } : {})
                    }}
                    sx={{
                        px: 1.5,
                        py: 1,
                        alignItems: 'flex-start',
                        /**
                         * The editing area uses the filled-input tint to match
                         * the app's filled fields; toolbar and footer stay on
                         * paper.
                         */
                        bgcolor: (theme) =>
                            readOnly
                                ? 'transparent'
                                : theme.palette.mode === 'light'
                                  ? 'rgba(0, 0, 0, 0.06)'
                                  : 'rgba(255, 255, 255, 0.09)',
                        ...(fullscreen && { flexGrow: 1, overflow: 'auto' })
                    }}
                />
            )}

            {/* Footer: validation message (left) + character counter (right). */}
            {!readOnly && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 1,
                        px: 1.5,
                        py: 0.5,
                        borderTop: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: hasError ? 'error.main' : 'text.secondary'
                        }}
                    >
                        {errorMessage ?? ''}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ flexShrink: 0, color: 'text.secondary' }}
                    >
                        {counterText}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

/**
 * A multiline text field with a toolbar (Textbausteine + dictation), a character
 * counter and a fullscreen editor for mobile. Every toolbar tool is optional: the
 * phrase button appears only with a `library`, the mic only where the browser
 * supports the Web Speech API.
 */
export default function RichTextArea<TFieldValues extends FieldValues>(
    props: RichTextAreaProps<TFieldValues>
) {
    const [fullscreen, setFullscreen] = useState(false);
    const [view, setView] = useState<TranslationView>('original');
    return (
        <>
            <RichTextAreaBody
                {...props}
                fullscreen={false}
                onToggleFullscreen={() => setFullscreen(true)}
                view={view}
                onViewChange={setView}
            />
            <Dialog
                open={fullscreen}
                onClose={() => setFullscreen(false)}
                fullScreen
            >
                <Box sx={{ height: '100%', p: { xs: 1.5, sm: 2 } }}>
                    <RichTextAreaBody
                        {...props}
                        fullscreen
                        onToggleFullscreen={() => setFullscreen(false)}
                        view={view}
                        onViewChange={setView}
                    />
                </Box>
            </Dialog>
        </>
    );
}
